import type { PropsWithChildren } from 'react';
import { useRef } from 'react';
import loadable from '@loadable/component';
import type { IGetProps, PageFC } from '@salesforce/pwa-kit-react-sdk';
import type { Location } from 'history';
import { ssrBffGet } from '../../bff-api/bff';
import { BFFHttpError } from '../../bff-api/model/BFFHttpError';
import { Layout } from '../../components/layout/Layout';
import { buildAssets } from '../../components/photo-grid/utils';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isValidSKU } from '../../utils/is-valid-sku';
import { PREVIEW_MODE_PARAM } from '../../utils/preview-mode-utils';
import { sfccGet } from '../../utils/sitegenesis';
import { isServer, noop } from '../../utils/utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { PDPSkeleton } from './PDPSkeleton';
import type { Product } from '../../bff-api/products/model';
import type { AssetInfo } from '../../components/photo-grid/utils';
import type { PDPLocationState } from './types';

const PDP = loadable(() => import('./PDP'));

type PDPLazyProps = PropsWithChildren & {
    product?: Product;
    assets?: AssetInfo[];
    isRedirected?: boolean;
};

const PDPLazy: PageFC<
    PDPLazyProps,
    {
        location: Location<PDPLocationState>;
    }
> = (props) => {
    const headerRef = useRef<HTMLDivElement | null>(null);

    return (
        <Layout headerRef={headerRef} className="mb-8">
            <PDP {...props} fallback={<PDPSkeleton />} headerRef={headerRef} />
        </Layout>
    );
};

PDPLazy.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

PDPLazy.getProps = async ({ req, res, params }: IGetProps): Promise<PDPLazyProps> => {
    try {
        if (!isValidSKU(params.sku)) {
            // Make sure we do not throw the error from the server side when in preview mode
            const urlParams = new URLSearchParams(req?.url?.split('?')[1] || '');
            const isSSRPreviewMode = !!urlParams.get(PREVIEW_MODE_PARAM);
            if (isSSRPreviewMode) {
                return {};
            }
            throw new BFFHttpError(404, 'Not Found', {});
        }

        const product = await ssrBffGet<Product>(`/products/${params.sku}`, {
            headers: {
                'Cache-Control': 'no-store',
            },
        });

        if (
            product.c_pwaUrl &&
            `/product/${params.pageURL}/${params.sku}` !== encodeURI(product.c_pwaUrl) &&
            typeof res?.redirect === 'function'
        ) {
            const theUrlMatchesTheSku = new RegExp(`.*/${params.sku}$`).test(product.c_pwaUrl);
            res.redirect(product.type?.master || theUrlMatchesTheSku ? 301 : 302, product.c_pwaUrl);
            res.setHeader = noop as unknown as typeof res.setHeader;
            return {};
        }

        configHtmlCache(res);
        const isBrandSpecialistVideosEnabled = await sfccGet<boolean>('PwaCustomPreference-Get', [
            { preference: CustomPreference.BRAND_SPECIALIST_VIDEO_ENABLED },
        ]);
        const assets = await buildAssets(product, { isBrandSpecialistVideosEnabled });
        return {
            product,
            assets,
        };
    } catch (e) {
        return {};
    }
};

export { PDPLazy };
